import React, { useState, useRef, useCallback, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from 'lucide-react';

const TOTAL_PAGES = 32;
const STORY_PAGES = Array.from({ length: TOTAL_PAGES }, (_, i) => ({
  src: `/curi-story-pages/page-${String(i + 1).padStart(2, '0')}.jpg`,
  alt: `Curi the Kitten - Page ${i + 1}`
}));

// Individual page component - must use forwardRef for react-pageflip
const Page = React.forwardRef(({ src, alt, pageNum }, ref) => (
  <div ref={ref} className="page-content" data-testid={`flipbook-page-${pageNum}`}>
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-contain"
      loading={pageNum < 4 ? 'eager' : 'lazy'}
      draggable={false}
    />
  </div>
));

Page.displayName = 'Page';

export const StoryFlipbook = ({ themeColor = '#f59e0b' }) => {
  const bookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bookSize, setBookSize] = useState({ width: 400, height: 600 });
  const containerRef = useRef(null);

  // Calculate book size based on container
  const updateBookSize = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const maxW = isFullscreen ? window.innerWidth * 0.9 : Math.min(rect.width - 32, 900);
    // Aspect ratio of pages: 1035x1575 ≈ 0.657
    const pageRatio = 1035 / 1575;
    const maxH = isFullscreen ? window.innerHeight * 0.85 : Math.min(600, window.innerHeight * 0.6);
    let pageW = maxW / 2; // Two pages shown side by side
    let pageH = pageW / pageRatio;
    if (pageH > maxH) {
      pageH = maxH;
      pageW = pageH * pageRatio;
    }
    // On small screens, single page mode
    if (window.innerWidth < 768) {
      pageW = Math.min(maxW - 16, 380);
      pageH = pageW / pageRatio;
      if (pageH > maxH) {
        pageH = maxH;
        pageW = pageH * pageRatio;
      }
    }
    setBookSize({ width: Math.round(pageW), height: Math.round(pageH) });
  }, [isFullscreen]);

  useEffect(() => {
    updateBookSize();
    window.addEventListener('resize', updateBookSize);
    return () => window.removeEventListener('resize', updateBookSize);
  }, [updateBookSize]);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
  }, []);

  const flipPrev = () => bookRef.current?.pageFlip()?.flipPrev();
  const flipNext = () => bookRef.current?.pageFlip()?.flipNext();

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col items-center ${isFullscreen ? 'fixed inset-0 z-[9999] bg-black/95 pt-4 justify-center' : ''}`}
      data-testid="story-flipbook-container"
    >
      {/* Fullscreen close button */}
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          data-testid="flipbook-close-fullscreen"
        >
          <X className="w-6 h-6" />
        </button>
      )}

      {/* Book */}
      <div className="relative flex items-center justify-center">
        {/* Prev button */}
        <button
          onClick={flipPrev}
          disabled={currentPage === 0}
          className="absolute left-0 md:-left-14 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          style={{ color: themeColor }}
          data-testid="flipbook-prev-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <HTMLFlipBook
          ref={bookRef}
          width={bookSize.width}
          height={bookSize.height}
          size="fixed"
          minWidth={280}
          maxWidth={600}
          minHeight={400}
          maxHeight={900}
          showCover={true}
          mobileScrollSupport={true}
          onFlip={onFlip}
          className="flipbook-shadow"
          style={{ margin: '0 auto' }}
          flippingTime={600}
          usePortrait={window.innerWidth < 768}
          startPage={0}
          drawShadow={true}
          maxShadowOpacity={0.3}
        >
          {STORY_PAGES.map((page) => (
            <Page key={page.src} src={page.src} alt={page.alt} pageNum={STORY_PAGES.indexOf(page) + 1} />
          ))}
        </HTMLFlipBook>

        {/* Next button */}
        <button
          onClick={flipNext}
          disabled={currentPage >= TOTAL_PAGES - 1}
          className="absolute right-0 md:-right-14 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          style={{ color: themeColor }}
          data-testid="flipbook-next-btn"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-4 mt-4 px-4 py-2 rounded-full bg-white/90 shadow-md backdrop-blur-sm" data-testid="flipbook-controls">
        <span className="text-sm font-medium text-gray-600 min-w-[80px] text-center">
          Page {currentPage + 1} / {TOTAL_PAGES}
        </span>
        <div className="w-px h-5 bg-gray-300" />
        <button
          onClick={toggleFullscreen}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          data-testid="flipbook-fullscreen-btn"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default StoryFlipbook;
